import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';

// CC: global polarity map — defines whether an increase in a given series label is good, bad, or neutral
const DELTA_POLARITY_MAP = {
	// Campaign Metrics (threat simulation)
	'Sent': 'neutral',
	'Read': 'negative',
	'Acknowledge': 'positive',
	'Link Clicked': 'negative',
	'Phished Login': 'negative',
	'User Reported': 'positive',
	'Attachment Downloaded': 'negative',
	// Phishing Campaign Success Trends
	'Success': 'positive',
	// Outbound Trends
	'Total': 'neutral',
	'Secured': 'neutral',
	'Quarantined': 'negative',
	// Deliverability to Recipients
	'Opened': 'neutral',
	'Complaint': 'negative',
	'Bounced': 'negative',
	// Account Takeover — incident type chart categories (more incidents = worse)
	'Suspicious device change': 'negative',
	'Suspicious location change': 'negative',
	'Abnormal email sending rate': 'negative',
	'Outbound burst to multiple domains': 'negative',
	'Sensitive data sent to new domains': 'negative',
	'Sensitive data sent to free email domain': 'negative',
	'Mailbox email password breach': 'negative',
	'Suspicious mailbox rules': 'negative',
	'Outbound malicious quarantine': 'negative',
	'Increased recipient complaint rate': 'negative',
	'Increased recipient hard bounce rate': 'negative',
	'Abnormal user activity': 'negative',
};

@Injectable({providedIn: 'root'})
export class TrendsComparisonService {

	comparisonActive = false;
	comparisonLoading = false;
	comparisonError = false;
	comparisonRunTriggered = false;
	isGhostMode = false;
	comparisonProgressPercent = 0;

	granularity = 'monthly';
	currentPeriodDate: Date = new Date();
	comparedPeriodDate: Date = new Date();

	// the ghost component instance (hidden, no render) used to run fetch+parse+prepare for comparison data
	ghostInstance: any = null;

	// stores the chart option variable names and their comparison values extracted from the ghost
	comparisonChartOptions: any = {};

	// emitted when the user confirms comparison periods
	comparisonRequested$ = new Subject<any>();
	// emitted when comparison data is ready (ghost finished preparing)
	comparisonDataReady$ = new Subject<any>();
	// emitted when comparison mode is deactivated
	comparisonDeactivated$ = new Subject<void>();
	// emitted when the user aborts batch loading without exiting comparison mode
	comparisonBatchAbort$ = new Subject<void>();

	setDefaultDates(granularity) {
		const now = new Date();
		this.comparedPeriodDate = new Date(now);
		if (granularity === 'daily') {
			this.currentPeriodDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
		}
		else if (granularity === 'weekly') {
			this.currentPeriodDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
		}
		else {
			this.currentPeriodDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
		}
	}

	getDateRange(date, granularity) {
		const d = new Date(date);
		let start, end;

		if (granularity === 'daily') {
			start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
			end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
		}
		else if (granularity === 'weekly') {
			const day = d.getDay();
			start = new Date(d);
			start.setDate(d.getDate() - day);
			start.setHours(0, 0, 0, 0);
			end = new Date(start);
			end.setDate(start.getDate() + 6);
			end.setHours(23, 59, 59, 999);
		}
		else {
			start = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0);
			end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
		}

		return {start, end};
	}

	activateComparison() {
		this.comparisonActive = true;
		this.comparisonLoading = true;
		this.comparisonRunTriggered = true;
		this.comparisonError = false;
		this.comparisonProgressPercent = 0;
		this.comparisonRequested$.next({
			granularity: this.granularity,
			currentRange: this.getDateRange(this.currentPeriodDate, this.granularity),
			comparedRange: this.getDateRange(this.comparedPeriodDate, this.granularity)
		});
	}

	abortComparisonBatches() {
		this.comparisonLoading = false;
		this.comparisonProgressPercent = 0;
		this.comparisonBatchAbort$.next();
	}

	deactivateComparison() {
		this.comparisonActive = false;
		this.comparisonLoading = false;
		this.comparisonError = false;
		this.comparisonRunTriggered = false;
		this.comparisonProgressPercent = 0;
		this.comparisonChartOptions = {};
		this.ghostInstance = null;
		this.comparisonDeactivated$.next();
	}

	onGhostDataReady(chartOptionsMap) {
		this.comparisonChartOptions = chartOptionsMap;
	}

	onGhostDataError() {
		this.comparisonLoading = false;
		this.comparisonError = true;
		this.deactivateComparison();
	}

	// compute deltas per x-axis category (total across all series per category).
	// Used for horizontal bar charts where bars represent named entities (emails, domains).
	calcCategoryDeltas(currentOptions, comparedOptions, defaultPolarity?) {
		const deltas = [];
		const categories = currentOptions?.xaxis?.categories || [];
		const currentSeries = currentOptions?.series || [];
		const comparedSeries = comparedOptions?.series || [];

		for (let ci = 0; ci < categories.length; ci++) {
			const currentTotal = currentSeries.reduce((sum, s) => sum + (s.data?.[ci] || 0), 0);
			const comparedTotal = comparedSeries.reduce((sum, s) => sum + (s.data?.[ci] || 0), 0);
			const label = categories[ci];
			deltas.push({
				label,
				change: this.calcPercentageChange(currentTotal, comparedTotal),
				diff: comparedTotal - currentTotal,
				polarity: DELTA_POLARITY_MAP[label] ?? defaultPolarity ?? 'neutral'
			});
		}
		return deltas;
	}

	// compute deltas between current chart series and comparison chart series
	calcSeriesDeltas(currentOptions, comparedOptions, defaultPolarity?) {
		const deltas = [];
		if (!currentOptions?.series || !comparedOptions?.series) {
			return deltas;
		}

		const currentSeries = currentOptions.series;
		const comparedSeries = comparedOptions.series;

		// handle radialBar / donut / pie (flat array of numbers)
		// also handle when current is empty but compared is flat — use compared as iteration base
		if (typeof currentSeries[0] === 'number' || (currentSeries.length === 0 && typeof comparedSeries[0] === 'number')) {
			const labels = (currentOptions.labels?.length ? currentOptions : comparedOptions).labels || [];
			// Use rawValues when present — series may be scaled display values (e.g. radialBar), not real counts.
			const currentValues = currentOptions.rawValues || currentSeries;
			const comparedValues = comparedOptions.rawValues || comparedSeries;
			const length = Math.max(currentValues.length, comparedValues.length);
			for (let i = 0; i < length; i++) {
				const label = labels[i] || `Series ${i + 1}`;
				deltas.push({
					label,
					change: this.calcPercentageChange(currentValues[i] || 0, comparedValues[i] || 0),
					diff: (comparedValues[i] || 0) - (currentValues[i] || 0),
					polarity: DELTA_POLARITY_MAP[label] ?? defaultPolarity ?? 'neutral'
				});
			}
		}
		else {
			// handle bar/area/line (array of {name, data})
			// when current has no series, use compared as the iteration base with 0 for current values
			const baseSeries = currentSeries.length ? currentSeries : comparedSeries;
			for (let i = 0; i < baseSeries.length; i++) {
				const currentTotal = (currentSeries[i]?.data || []).reduce((a, b) => a + b, 0);
				const comparedTotal = (comparedSeries[i]?.data || []).reduce((a, b) => a + b, 0);
				const label = baseSeries[i]?.name || `Series ${i + 1}`;
				deltas.push({
					label,
					change: this.calcPercentageChange(currentTotal, comparedTotal),
					diff: comparedTotal - currentTotal,
					polarity: DELTA_POLARITY_MAP[label] ?? defaultPolarity ?? 'neutral'
				});
			}
		}

		return deltas;
	}

	// compute percentage change — positive means compared is higher than current
	calcPercentageChange(current, compared) {
		if (!current || current === 0) {
			return compared > 0 ? 100 : 0;
		}
		return ((compared - current) / current) * 100;
	}
}
